
import React from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { createNewFloorPlan } from "@/services/floorPlanService";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface ActionCardsProps {
  hasCurrentPlan: boolean;
}

export function ActionCards({ hasCurrentPlan }: ActionCardsProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleContinueEditing = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('floor_plans')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (data) {
        navigate(`/?id=${data.id}`);
      } else {
        // Als er geen actieve plattegrond is, ga naar de hoofdpagina
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching active floor plan:', error);
      navigate('/');
    }
  };

  const handleNewPlan = async () => {
    if (!user) return;

    try {
      // Maak een nieuwe plattegrond aan
      await createNewFloorPlan(user.id);
      
      // Navigeer naar de editor met de new parameter om een reset te forceren
      navigate('/?new=true');
    } catch (error) {
      console.error('Error creating new floor plan:', error);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
      {hasCurrentPlan && (
        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Play className="h-5 w-5 text-[#1b9d3d]" />
              <h3 className="text-xl font-semibold">Verder gaan</h3>
            </div>
            <p className="text-gray-600 text-base mb-6">
              Ga verder met je huidige plattegrond
            </p>
            <Button 
              onClick={handleContinueEditing} 
              className="w-full bg-[#e6f5ea] text-[#1b9d3d] hover:bg-[#d1ebd7] font-medium text-base py-3"
            >
              Doorgaan met bewerken
            </Button>
          </div>
        </Card>
      )}
      
      <Card className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <FilePlus className="h-5 w-5 text-[#1b9d3d]" />
            <h3 className="text-xl font-semibold">Nieuwe plattegrond</h3>
          </div>
          <p className="text-gray-600 text-base mb-6">
            Begin met een lege plattegrond
          </p>
          <Button 
            onClick={handleNewPlan}
            className="w-full bg-[#1b9d3d] hover:bg-[#168533] text-white font-medium text-base py-3"
          >
            Nieuwe plattegrond maken
          </Button>
        </div>
      </Card>
    </div>
  );
}
